interface LocatorLabel {
    singular: string;
    plural: string;
}

// The seven canonical types are always present (literal property access like
// LOCATOR_LABELS.unit is non-undefined), while a dynamic lookup by an arbitrary
// normalised type may miss -- the index signature plus noUncheckedIndexedAccess
// reflects that honestly, so makeLocator's `if (!labels)` guard stays meaningful.
interface LocatorLabelMap {
    unit: LocatorLabel;
    lot: LocatorLabel;
    apartment: LocatorLabel;
    apt: LocatorLabel;
    flat: LocatorLabel;
    villa: LocatorLabel;
    townhouse: LocatorLabel;
    [type: string]: LocatorLabel;
}

const LOCATOR_LABELS: LocatorLabelMap = {
    unit: { singular: 'Unit', plural: 'Units' },
    lot: { singular: 'Lot', plural: 'Lots' },
    apartment: { singular: 'Apartment', plural: 'Apartments' },
    apt: { singular: 'Apartment', plural: 'Apartments' },
    flat: { singular: 'Flat', plural: 'Flats' },
    villa: { singular: 'Villa', plural: 'Villas' },
    townhouse: { singular: 'Townhouse', plural: 'Townhouses' },
};

export interface UnitLocator {
    type: string;
    number: string;
    key: string;
    display: string;
}

// A scope item as read by this module: every field is optional and untrusted
// (scope rows arrive from AI output / Dexie / API and shapes drift).
interface ScopeItemLike {
    title?: unknown;
    description?: unknown;
    descriptionBrief?: unknown;
    description_brief?: unknown;
    clientDescription?: unknown;
    notes_to_client?: unknown;
    inclusions?: unknown;
}

interface LocatorFormData {
    unitNumber?: unknown;
    lotNumber?: unknown;
    scopeObjective?: unknown;
    jobDescription?: unknown;
    notes?: unknown;
    inspectorNotes?: unknown;
    address?: unknown;
}

interface LocatorProjectSummary {
    scope_objective?: unknown;
    scopeObjective?: unknown;
    summary?: unknown;
    job_description?: unknown;
    projectDescription?: unknown;
}

// Return type is intentionally `unknown`: the object branch returns the raw
// `.text || .label || .description` value as-is (preserved verbatim from the
// pre-TS source). Callers coerce via String(...)/.join(...), so non-string
// truthy values are passed through unchanged rather than silently stringified.
function inclusionText(inclusion: unknown): unknown {
    if (typeof inclusion === 'string') return inclusion;
    if (inclusion && typeof inclusion === 'object') {
        const obj = inclusion as { text?: unknown; label?: unknown; description?: unknown };
        return obj.text || obj.label || obj.description || '';
    }
    return '';
}

function normaliseNumber(value: unknown): string {
    return String(value || '')
        .replace(/^[#\s]+/, '')
        .replace(/[^\da-z]/gi, '')
        .toUpperCase();
}

function normaliseType(type: unknown): string {
    const raw = String(type || '').toLowerCase();
    if (raw === 'units') return 'unit';
    if (raw === 'lots') return 'lot';
    if (raw === 'apartments') return 'apartment';
    if (raw === 'apts') return 'apt';
    if (raw === 'flats') return 'flat';
    if (raw === 'villas') return 'villa';
    if (raw === 'townhouses') return 'townhouse';
    return raw;
}

function makeLocator(type: unknown, number: unknown): UnitLocator | null {
    const normalisedType = normaliseType(type);
    const normalisedNumber = normaliseNumber(number);
    const labels = LOCATOR_LABELS[normalisedType];
    if (!labels || !normalisedNumber) return null;
    return {
        type: normalisedType === 'apt' ? 'apartment' : normalisedType,
        number: normalisedNumber,
        key: `${normalisedType === 'apt' ? 'apartment' : normalisedType}:${normalisedNumber}`,
        display: `${labels.singular} ${normalisedNumber}`,
    };
}

function splitNumberList(value: unknown): string[] {
    return String(value || '')
        .split(/\s*(?:,|&|\band\b)\s*/i)
        .map(normaliseNumber)
        .filter(Boolean);
}

function addLocator(target: Map<string, UnitLocator>, type: unknown, number: unknown): void {
    const locator = makeLocator(type, number);
    if (locator && !target.has(locator.key)) target.set(locator.key, locator);
}

export function extractUnitLocatorsFromText(text: unknown): UnitLocator[] {
    const source = String(text || '');
    if (!source.trim()) return [];
    const found = new Map<string, UnitLocator>();
    const locatorPattern = /\b(unit|units|lot|lots|apartment|apartments|apt|apts|flat|flats|villa|villas|townhouse|townhouses)\s*(?:no\.?|number|#)?\s*([a-z]?\d+[a-z]?(?:\s*(?:,|&|\band\b)\s*[a-z]?\d+[a-z]?){0,8})\b/gi;
    let match: RegExpExecArray | null;
    while ((match = locatorPattern.exec(source)) !== null) {
        const type = match[1];
        for (const number of splitNumberList(match[2])) {
            addLocator(found, type, number);
        }
    }
    return Array.from(found.values());
}

export function extractUnitLocatorsFromScope(scopeItems: unknown[] = []): UnitLocator[] {
    const found = new Map<string, UnitLocator>();
    const items: ScopeItemLike[] = (Array.isArray(scopeItems) ? scopeItems : []) as ScopeItemLike[];
    for (const item of items) {
        const text = [
            item?.title,
            item?.description,
            item?.descriptionBrief,
            item?.description_brief,
            item?.clientDescription,
            item?.notes_to_client,
            ...(Array.isArray(item?.inclusions) ? item.inclusions.map(inclusionText) : []),
        ].filter(Boolean).join(' ');
        for (const locator of extractUnitLocatorsFromText(text)) {
            if (!found.has(locator.key)) found.set(locator.key, locator);
        }
    }
    return Array.from(found.values());
}

export function collectScopeUnitLocators({
    formData = {},
    projectSummary = {},
    scopeItems = [],
}: {
    formData?: LocatorFormData;
    projectSummary?: LocatorProjectSummary;
    scopeItems?: unknown[];
} = {}): UnitLocator[] {
    const explicitUnit = String(formData?.unitNumber || '').trim();
    const explicitLot = String(formData?.lotNumber || '').trim();
    const textSources = [
        explicitUnit ? `Unit ${explicitUnit.replace(/^unit\s*/i, '')}` : '',
        explicitLot ? `Lot ${explicitLot.replace(/^lot\s*/i, '')}` : '',
        formData?.scopeObjective,
        formData?.jobDescription,
        formData?.notes,
        formData?.inspectorNotes,
        formData?.address,
        projectSummary?.scope_objective,
        projectSummary?.scopeObjective,
        projectSummary?.summary,
        projectSummary?.job_description,
        projectSummary?.projectDescription,
    ];
    const found = new Map<string, UnitLocator>();
    for (const text of textSources) {
        for (const locator of extractUnitLocatorsFromText(text)) {
            if (!found.has(locator.key)) found.set(locator.key, locator);
        }
    }
    for (const locator of extractUnitLocatorsFromScope(scopeItems)) {
        if (!found.has(locator.key)) found.set(locator.key, locator);
    }
    return Array.from(found.values());
}

/**
 * The unit or lot a site address names: the first number after its first
 * Unit or Lot word. "Unit 17, 56 Dalleys Road" is Unit 17 and "Unit 3/12
 * Smith Street" is Unit 3; the street number is never a unit.
 */
export function addressUnitLocator(address: unknown): UnitLocator | null {
    const match = String(address || '').match(
        /\b(unit|lot|apartment|apt|flat|villa|townhouse)\s*(?:no\.?|number|#)?\s*([a-z]?\d+[a-z]?)\b/i,
    );
    return match ? makeLocator(match[1], match[2]) : null;
}

export interface ObjectiveUnitLocatorReview {
    /** Units or lots in the job's Unit and Lot fields that the objective does not name. */
    missing: UnitLocator[];
    /** Units or lots the objective names that appear nowhere else in the job. */
    notInJob: UnitLocator[];
}

/**
 * Measures the objective's units and lots against the job. It never rewrites
 * the objective. A stored objective is not a source, so a unit an old
 * objective invented is not confirmed by itself.
 */
export function reviewObjectiveUnitLocators({
    objective,
    formData = {},
    scopeItems = [],
}: {
    objective?: unknown;
    formData?: LocatorFormData;
    scopeItems?: unknown[];
} = {}): ObjectiveUnitLocatorReview {
    const named = extractUnitLocatorsFromText(objective);
    const namedKeys = new Set(named.map((locator) => locator.key));
    const explicit = collectScopeUnitLocators({
        formData: { unitNumber: formData?.unitNumber, lotNumber: formData?.lotNumber },
    });
    const known = new Map<string, UnitLocator>();
    const remember = (locator: UnitLocator | null) => {
        if (locator && !known.has(locator.key)) known.set(locator.key, locator);
    };
    explicit.forEach(remember);
    remember(addressUnitLocator(formData?.address));
    for (const text of [formData?.jobDescription, formData?.notes, formData?.inspectorNotes]) {
        extractUnitLocatorsFromText(text).forEach(remember);
    }
    extractUnitLocatorsFromScope(scopeItems).forEach(remember);
    return {
        missing: explicit.filter((locator) => !namedKeys.has(locator.key)),
        notInJob: named.filter((locator) => !known.has(locator.key)),
    };
}

function joinWords(parts: string[]): string {
    if (parts.length <= 1) return parts[0] || '';
    if (parts.length === 2) return `${parts[0]} and ${parts[1]}`;
    return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
}

export function formatUnitLocatorPhrase(locators: UnitLocator[] = []): string {
    const items = Array.isArray(locators) ? locators.filter(Boolean) : [];
    if (!items.length) return '';
    const first = items[0]!;
    const sameType = items.every((locator) => locator.type === first.type);
    if (sameType) {
        const labels = LOCATOR_LABELS[first.type] || LOCATOR_LABELS.unit;
        const label = items.length === 1 ? labels.singular : labels.plural;
        return `to ${label} ${joinWords(items.map((locator) => locator.number))}`;
    }
    return `to ${joinWords(items.map((locator) => locator.display))}`;
}

export function ensureObjectiveMentionsUnitLocators(objective: unknown, locators: UnitLocator[] = []): string {
    const value = String(objective || '').trim();
    const items = Array.isArray(locators) ? locators.filter(Boolean) : [];
    if (!value || !items.length) return value;
    const presentKeys = new Set(extractUnitLocatorsFromText(value).map((locator) => locator.key));
    const missing = items.filter((locator) => !presentKeys.has(locator.key));
    if (!missing.length) return value;
    const hasSomeLocator = presentKeys.size > 0;
    const phrase = formatUnitLocatorPhrase(hasSomeLocator ? missing : items);
    if (!phrase) return value;
    if (hasSomeLocator) {
        const addition = phrase.replace(/^to\s+/i, '');
        const withAnchor = value.replace(/\s+at the above (property|address|site)\b/i, ` and ${addition} at the above $1`);
        if (withAnchor !== value) return withAnchor;
        return value.replace(/[.!?]*\s*$/, `, including ${addition}.`);
    }
    const withAnchor = value.replace(/\s+at the above (property|address|site)\b/i, ` ${phrase} at the above $1`);
    if (withAnchor !== value) return withAnchor;
    return value.replace(/[.!?]*\s*$/, ` ${phrase}.`);
}
